import { PageText } from '@quizdb/common';
import {
  MaterialPrepError,
  OCR_WORKERS,
  OcrProgress,
  OpenPdfRenderer,
  PdfRenderer,
} from '../extraction/extractionTypes';
import { OcrEngine } from './ocrTypes';

/** What `ocrPdfPages` reads with: a render worker to open, and an OCR engine the caller owns. */
export interface OcrPdfPagesDeps {
  openRenderer: OpenPdfRenderer;
  ocr: OcrEngine;
}

const abortError = (): DOMException => new DOMException('Aborted', 'AbortError');

const isAbortError = (thrown: unknown): boolean =>
  thrown instanceof DOMException && thrown.name === 'AbortError';

/** Passes a `MaterialPrepError` or an `AbortError` through; anything else is `unknown`. */
const toPrepError = (thrown: unknown): unknown =>
  thrown instanceof MaterialPrepError || isAbortError(thrown)
    ? thrown
    : new MaterialPrepError('unknown');

/**
 * Reads `pages` of the PDF `file` by OCR (QZ-D92): renders each page in the render worker and
 * reads the image with `deps.ocr`. `Math.min(OCR_WORKERS, pages.length)` lanes each ask for
 * their next render only once their read has finished, so at most `OCR_WORKERS` rendered pages
 * exist at once and the scheduler gets at most one page per worker (PHO-D9, PHO-D10). Resolves
 * with every page's text in page order. Closes the renderer exactly once; never terminates
 * `deps.ocr`, whose lifetime the caller owns (PHO-D10).
 */
export const ocrPdfPages = async (
  file: Blob,
  pages: number[],
  deps: OcrPdfPagesDeps,
  onProgress: (progress: OcrProgress) => void,
  signal: AbortSignal
): Promise<PageText[]> => {
  if (signal.aborted) {
    throw abortError();
  }
  if (pages.length === 0) {
    return [];
  }

  let renderer: PdfRenderer;
  try {
    renderer = await deps.openRenderer(file, signal);
  } catch (thrown) {
    throw toPrepError(thrown);
  }

  return new Promise<PageText[]>((resolve, reject) => {
    const total = pages.length;
    const results: PageText[] = [];
    let nextIndex = 0;
    let settled = false;

    const settle = (outcome: { error: unknown } | { pages: PageText[] }): void => {
      if (settled) {
        return;
      }
      settled = true;
      // Closed before the listener goes, so an abort can never slip in between.
      try {
        renderer.close();
      } catch {
        // A failing close must not mask the outcome.
      }
      signal.removeEventListener('abort', onAbort);
      if ('error' in outcome) {
        reject(outcome.error);
      } else {
        resolve(outcome.pages);
      }
    };

    // Rejects at once; it does not wait for an in-flight read, since the caller terminates the
    // engine.
    const onAbort = (): void => settle({ error: abortError() });

    if (signal.aborted) {
      settle({ error: abortError() });
      return;
    }
    signal.addEventListener('abort', onAbort);

    const lane = async (): Promise<void> => {
      while (!settled && nextIndex < total) {
        const page = pages[nextIndex++];
        let image: Blob | undefined = await renderer.render(page);
        if (settled) {
          return;
        }
        const text = await deps.ocr.read(image);
        image = undefined;
        if (settled) {
          return;
        }
        results.push({ page, text });
        onProgress({ done: results.length, total });
      }
    };

    try {
      onProgress({ done: 0, total });
    } catch (thrown) {
      settle({ error: toPrepError(thrown) });
      return;
    }

    const lanes = Array.from({ length: Math.min(OCR_WORKERS, total) }, () =>
      // Every lane's rejection is caught here, so a render or read that loses the race after a
      // failure or an abort cannot become an unhandled rejection.
      lane().catch((thrown: unknown) => settle({ error: toPrepError(thrown) }))
    );
    void Promise.all(lanes).then(() =>
      settle({ pages: [...results].sort((a, b) => a.page - b.page) })
    );
  });
};
